// AI Office backend calls. Uses the web app's axios setup (cookie login + company on GET requests).
import axios from '../axios';

const url = (path) => `${window.backendUrl}/ai-office${path}`;
const get = (path, params) => axios.get(url(path), { params }).then((r) => r.data);

export const getState = () => get('/state');
export const getAgent = (id) => get(`/agents/${encodeURIComponent(id)}`);
export const getApprovals = (status = 'pending') => get('/approvals', { status });
export const getJobs = () => get('/jobs');
export const getHistory = () => get('/history');
export const getSettings = () => get('/settings');
export const getConversation = (id) => get(`/agents/${encodeURIComponent(id)}/conversation`);
export const getOfficeLife = () => get('/office-life');
export const sendMessage = (id, text, viaVoice = false, files = []) =>
  axios.post(url(`/agents/${encodeURIComponent(id)}/messages`), {
    text, viaVoice, attachments: files.map((f) => ({ name: f.name, type: f.type, data: f.data })),
  }).then((r) => r.data);

export const approve = (id, editedArgs) =>
  axios.post(url(`/approvals/${id}/approve`), { editedArgs: editedArgs || {} }).then((r) => r.data);
export const reject = (id, reason) => axios.post(url(`/approvals/${id}/reject`), { reason }).then((r) => r.data);
export const addMemory = (agentId, fact, shared) => axios.post(url('/memories'), { agentId, fact, shared: !!shared }).then((r) => r.data);
export const forgetMemory = (id) => axios.delete(url(`/memories/${id}`)).then((r) => r.data);
export const hireAgent = (body) => axios.post(url('/agents'), body).then((r) => r.data);
export const getTools = () => get('/tools');
export const startBrief = () => axios.post(url('/brief'), {}).then((r) => r.data);
export const patchSettings = (changes) => axios.patch(url('/settings'), changes).then((r) => r.data);
export const patchAgent = (id, changes) => axios.patch(url(`/agents/${encodeURIComponent(id)}`), changes).then((r) => r.data);

// Live updates (status lights, bubbles, streamed answers). Calls onEvent(event) for each one; the browser
// reconnects by itself if the connection drops. Returns a function that closes the stream.
export function openEvents(onEvent, onState) {
  const es = new EventSource(url('/events'), { withCredentials: true });
  es.onmessage = (m) => { try { onEvent(JSON.parse(m.data)); } catch (_) { /* ignore a bad line */ } };
  es.onopen = () => onState && onState(true);
  es.onerror = () => onState && onState(false);
  return () => es.close();
}

// Turns an axios error into one short sentence for the screen.
export function errorText(err) {
  const status = err && err.response && err.response.status;
  const serverSays = err && err.response && err.response.data && err.response.data.error;
  if (serverSays && status !== 401) return serverSays;
  if (status === 401 || status === 403) return (status === 403 ? 'Your login is not allowed to do this.' : 'Your login has expired. Log in again from the main window.');
  if (status === 503) return 'AI Office is starting up. Try again in a moment.';
  return (err && err.response && err.response.data && err.response.data.error) || 'Could not reach the server. Check the connection and try again.';
}

// Voice calls (Gemini Live): a one-use token for the call, each request the employee works on, and the call length.
export const voiceSession = (agentId) => axios.post(url('/voice/session'), { agentId }).then((r) => r.data);
export const voiceTurn = (agentId, text) => axios.post(url(`/agents/${encodeURIComponent(agentId)}/voice-turn`), { text }).then((r) => r.data);
export const voiceUsage = (agentId, seconds) => axios.post(url('/voice/usage'), { agentId, seconds }).then((r) => r.data);
